/**
 * ItemsTab — extracted from SalesOrderCreate.js.
 * Receives all needed state + handlers via a single `ctx` prop bag
 * so the parent page retains single-source-of-truth.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TabsContent } from '../../components/ui/tabs';
import { AlertTriangle, Calculator, CheckCircle2, Loader2, Package, Plus, Trash2, Warehouse, XCircle } from 'lucide-react';

const ItemsTab = ({ ctx }) => {
  const {
    header,
    items,
    materials,
    plants,
    storageLocations,
    atpResults,
    isCheckingATP,
    activeItemIndex,
    setActiveItemIndex,
    handleItemChange,
    handleATPCheck,
    handleAddItem,
    handleRemoveItem,
    handleAllATPCheck,
    currentItem,
  } = ctx;

  return (
          <TabsContent value="items">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Item List */}
              <Card className="p-4 lg:col-span-1">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Items</h3>
                  <Button size="sm" onClick={handleAddItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={`item-${item.item_number}-${index}`}
                      className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${
                        activeItemIndex === index ? 'bg-orange-100 border-2 border-orange-500' : 'bg-gray-50 hover:bg-gray-100'
                      } ${atpResults[index]?.can_fulfill === false ? 'border-l-4 border-l-red-500' : atpResults[index]?.can_fulfill === true ? 'border-l-4 border-l-green-500' : ''}`}
                      onClick={() => setActiveItemIndex(index)}
                    >
                      <div>
                        <p className="font-mono font-bold text-sm">{item.item_number.toString().padStart(4, '0')}</p>
                        <p className="text-xs text-gray-600 truncate max-w-[120px]">
                          {item.item_description || item.material_id || 'New Item'}
                        </p>
                        {atpResults[index] && (
                          <div className="mt-1">
                            <p className={`text-xs ${atpResults[index].can_fulfill ? 'text-green-600' : 'text-red-600'}`}>
                              {atpResults[index].can_fulfill ? '✓ Available' : `✗ Short: ${atpResults[index].shortage_quantity}`}
                            </p>
                            {atpResults[index].confirmed_date && (
                              <p className={`text-xs ${atpResults[index].can_meet_requested_date ? 'text-green-600' : 'text-amber-600'}`}>
                                📅 {atpResults[index].confirmed_date}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {items.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full" 
                    onClick={handleAllATPCheck}
                    disabled={isCheckingATP || items.filter(i => i.material_id).length === 0}
                  >
                    {isCheckingATP ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                    ) : (
                      <><Warehouse className="w-4 h-4 mr-2" /> Check All ATP</>
                    )}
                  </Button>
                </div>
              </Card>

              {/* Item Details */}
              <Card className="p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Item {currentItem.item_number.toString().padStart(4, '0')} Details
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Material</Label>
                    <Select 
                      value={currentItem.material_id || '__NONE__'} 
                      onValueChange={(v) => handleItemChange(activeItemIndex, 'material_id', v === '__NONE__' ? '' : v)}
                    >
                      <SelectTrigger><SelectValue placeholder="Select Material" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- Manual Entry --</SelectItem>
                        {materials.map(m => (
                          <SelectItem key={m.material_number} value={m.material_number}>
                            {m.material_number} - {m.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Item Category</Label>
                    <Select value={currentItem.item_category} onValueChange={(v) => handleItemChange(activeItemIndex, 'item_category', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TAN">TAN - Standard Item</SelectItem>
                        <SelectItem value="TANN">TANN - Free Item</SelectItem>
                        <SelectItem value="TAD">TAD - Service Item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Description *</Label>
                    <Input
                      value={currentItem.item_description}
                      onChange={(e) => handleItemChange(activeItemIndex, 'item_description', e.target.value)}
                      placeholder="Item description"
                      maxLength={100}
                      required
                    />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={currentItem.order_quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow whole numbers
                        if (value === '' || /^\d+$/.test(value)) {
                          handleItemChange(activeItemIndex, 'order_quantity', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point and 'e' for scientific notation
                        if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Whole numbers only</p>
                  </div>
                  <div>
                    <Label>Unit of Measure</Label>
                    <Input
                      value={currentItem.unit_of_measure || 'EA'}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">Derived from material master</p>
                  </div>
                  <div>
                    <Label>Plant</Label>
                    <Select 
                      value={currentItem.plant || '__NONE__'} 
                      onValueChange={(v) => {
                        handleItemChange(activeItemIndex, 'plant', v === '__NONE__' ? '' : v);
                        // Clear storage location when plant changes
                        handleItemChange(activeItemIndex, 'storage_location', '');
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select Plant" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {plants.map(p => (
                          <SelectItem key={p.plant_id} value={p.plant_id}>
                            {p.plant_id} - {p.plant_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Storage Location</Label>
                    <Select 
                      value={currentItem.storage_location || '__NONE__'} 
                      onValueChange={(v) => handleItemChange(activeItemIndex, 'storage_location', v === '__NONE__' ? '' : v)}
                      disabled={!currentItem.plant}
                    >
                      <SelectTrigger data-testid="storage-location-select">
                        <SelectValue placeholder={currentItem.plant ? "Select Storage Location" : "Select Plant first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {storageLocations
                          .filter(sl => sl.plant_id === currentItem.plant)
                          .map(sl => (
                            <SelectItem key={sl.storage_location_id} value={sl.storage_location_id}>
                              {sl.storage_location_id} - {sl.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {currentItem.plant && storageLocations.filter(sl => sl.plant_id === currentItem.plant).length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No storage locations found for this plant</p>
                    )}
                  </div>
                  <div>
                    <Label>Item Requested Delivery Date</Label>
                    <Input
                      type="date"
                      value={currentItem.requested_delivery_date}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        const today = new Date().toISOString().split('T')[0];
                        if (selectedDate && selectedDate < today) {
                          toast.error('Requested Delivery Date cannot be in the past');
                          return;
                        }
                        handleItemChange(activeItemIndex, 'requested_delivery_date', selectedDate);
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {currentItem.requested_delivery_date 
                        ? <span className="text-green-600">Item date set - takes priority over header</span>
                        : header.requested_delivery_date 
                          ? <span>Using header date: {header.requested_delivery_date}</span>
                          : <span>No date set - will use ATP calculation</span>
                      }
                    </p>
                  </div>
                </div>
                
                {/* ATP Check Section */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <Warehouse className="w-5 h-5 text-gray-700" />
                      <span className="font-medium">Availability Check (ATP)</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleATPCheck(activeItemIndex)}
                      disabled={!currentItem.material_id || isCheckingATP}
                      data-testid="check-item-atp-btn"
                    >
                      {isCheckingATP ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                      ) : (
                        <>Check Availability</>
                      )}
                    </Button>
                  </div>
                  
                  {atpResults[activeItemIndex] ? (
                    <div className={`p-3 rounded-lg ${atpResults[activeItemIndex].can_fulfill ? 'bg-green-100' : atpResults[activeItemIndex].status === 'PARTIAL' ? 'bg-amber-100' : 'bg-red-100'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {atpResults[activeItemIndex].can_fulfill ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : atpResults[activeItemIndex].status === 'PARTIAL' ? (
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-medium ${atpResults[activeItemIndex].can_fulfill ? 'text-green-800' : atpResults[activeItemIndex].status === 'PARTIAL' ? 'text-amber-800' : 'text-red-800'}`}>
                          {atpResults[activeItemIndex].status === 'AVAILABLE' ? 'Stock Available' :
                           atpResults[activeItemIndex].status === 'PARTIAL' ? 'Partially Available' : 'Not Available - Replenishment Required'}
                        </span>
                      </div>
                      
                      {/* Stock Information */}
                      <div className="grid grid-cols-4 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-600">Requested</p>
                          <p className="font-mono">{atpResults[activeItemIndex].requested_quantity}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Available</p>
                          <p className="font-mono text-green-700">{atpResults[activeItemIndex].available_quantity}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Reserved</p>
                          <p className="font-mono">{atpResults[activeItemIndex].reserved_quantity}</p>
                        </div>
                        {atpResults[activeItemIndex].shortage_quantity > 0 && (
                          <div>
                            <p className="text-gray-600">Shortage</p>
                            <p className="font-mono text-red-600">{atpResults[activeItemIndex].shortage_quantity}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Delivery Date Information */}
                      <div className="border-t pt-3 mt-3">
                        <h4 className="font-medium text-gray-700 mb-2">Delivery Schedule</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Requested Date</p>
                            <p className="font-mono">{atpResults[activeItemIndex].requested_date || 'Not specified'}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Confirmed Date</p>
                            <p className={`font-mono font-bold ${atpResults[activeItemIndex].can_meet_requested_date ? 'text-green-700' : 'text-amber-700'}`}>
                              {atpResults[activeItemIndex].confirmed_date}
                              {!atpResults[activeItemIndex].can_meet_requested_date && ' (Delayed)'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Lead Time Breakdown */}
                        {atpResults[activeItemIndex].delivery_calculation_method && (
                          <div className="mt-2 p-2 bg-white bg-opacity-50 rounded text-xs">
                            <p className="text-gray-600 mb-1">Calculation Method:</p>
                            <p className="font-mono text-gray-800">{atpResults[activeItemIndex].delivery_calculation_method}</p>
                            <div className="flex flex-wrap gap-3 mt-1 text-gray-600">
                              {atpResults[activeItemIndex].procurement_type && (
                                <span>Procurement: {atpResults[activeItemIndex].procurement_type === 'E' ? 'In-house' : atpResults[activeItemIndex].procurement_type === 'F' ? 'External' : 'Both'}</span>
                              )}
                              <span>Loading: {atpResults[activeItemIndex].loading_time}d</span>
                              <span>Pick/Pack: {atpResults[activeItemIndex].pick_pack_time}d</span>
                              {atpResults[activeItemIndex].gr_processing_time > 0 && (
                                <span>GR Processing: {atpResults[activeItemIndex].gr_processing_time}d</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Warnings for Missing Master Data */}
                        {atpResults[activeItemIndex].warnings && atpResults[activeItemIndex].warnings.length > 0 && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="atp-warnings">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-semibold text-amber-800">Master Data Warnings</span>
                              {atpResults[activeItemIndex].using_defaults && (
                                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded">Using Defaults</span>
                              )}
                            </div>
                            <ul className="text-xs text-amber-700 space-y-1 ml-6">
                              {atpResults[activeItemIndex].warnings.map((warning, idx) => (
                                <li key={`warning-${activeItemIndex}-${idx}-${warning.slice(0, 16)}`} className="list-disc">{warning}</li>
                              ))}
                            </ul>
                            <p className="text-xs text-amber-600 mt-2 italic">
                              To improve accuracy, maintain the missing lead time fields in Material Master (MRP & Sales views).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Select a material and click "Check Availability" to verify stock and get confirmed delivery date</p>
                  )}
                </div>

                {/* Pricing Note */}
                <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-800">
                    <Calculator className="w-5 h-5" />
                    <span className="font-medium">Automatic Pricing</span>
                  </div>
                  <p className="text-sm text-orange-700 mt-1">
                    Pricing will be calculated automatically based on the selected Pricing Procedure ({header.pricing_procedure}),
                    Material, Customer, and Sales Organization when the order is saved.
                  </p>
                </div>
              </Card>
            </div>
          </TabsContent>
  );
};

export default ItemsTab;
